import type { IArticleWithOptions } from "@/data/commands/customer/AddArticleToCartCmd";
import type {
    ICustomerArticleWithOptions,
    ICustomerOptionGroup,
    ICustomerOptionGroupOption,
} from "@/data/model/Customer";

import { getPreselectedOptions } from "@/components/order/preselectedArticleOptions";

export function canBeAddedWithPreselectedOptions(article: ICustomerArticleWithOptions) {
    const preselectedOptions = getPreselectedOptions(article);

    if (!areSelectedOptionGroupsValid(article.options, preselectedOptions)) {
        return false;
    }

    return true;
}

export function areSelectedOptionGroupsValid(
    optionGroups: ICustomerOptionGroup[],
    selectedOptionGroups: IArticleWithOptions[]
) {
    for (const optionGroup of optionGroups) {
        const selectedOptionGroupOptions = selectedOptionGroups.filter(
            (o) => o.optionGroupId === optionGroup.optionGroupId
        );

        if (!isValidOptionGroup(optionGroup, selectedOptionGroupOptions)) {
            return false;
        }
    }

    return true;
}

function isValidOptionGroup(optionGroup: ICustomerOptionGroup, selectedOptions: IArticleWithOptions[]) {
    const isBelowMinimum = optionGroup.minOptions !== undefined && selectedOptions.length < optionGroup.minOptions;
    const isAboveMaximum = optionGroup.maxOptions !== undefined && selectedOptions.length > optionGroup.maxOptions;

    if (isBelowMinimum || isAboveMaximum) {
        return false;
    }

    for (const option of optionGroup.options) {
        const selectedOption = selectedOptions.find((o) => o.articleId === option.articleId);

        if (!isValidOption(option, selectedOption, optionGroup.maxPerOption)) {
            return false;
        }
    }

    return true;
}

function isValidOption(
    option: ICustomerOptionGroupOption,
    selectedOption: IArticleWithOptions | undefined,
    maxPerOption: number | undefined
) {
    const minQuantity = option.minQuantity ?? 0;

    // max quantity is the smaller of the max quantity of the item and the max quantity of the option
    const maxItemQuantity = option.maxQuantity ?? Number.MAX_SAFE_INTEGER;
    const maxPerOptionQuantity = maxPerOption ?? Number.MAX_SAFE_INTEGER;
    const maxQuantity = Math.min(maxItemQuantity, maxPerOptionQuantity);

    const selectedQuantity = selectedOption?.quantity ?? 0;

    const isQuantityValid = selectedQuantity >= minQuantity && selectedQuantity <= maxQuantity;
    if (!isQuantityValid) {
        return false;
    }

    if (selectedQuantity == 0) {
        return true;
    }

    const selectedOptionGroups = selectedOption?.options ?? [];
    if (!areSelectedOptionGroupsValid(option.optionGroups, selectedOptionGroups)) {
        return false;
    }

    return true;
}
